"use client"

import { ChevronLeft, ChevronRight } from "lucide-react"

import {
  bandShape,
  dayLabel,
  monthDays,
  monthLabel,
  WEEKDAYS,
  type YearMonth,
} from "@/lib/calendar"
import { cn } from "@/lib/utils"

/**
 * One month of days — canvas 31c's day cell, laid out as 31a's grid.
 *
 * The cell has seven states and no others: in-month, hovered, today, selected,
 * in range, out of month, unavailable. Out-of-month days *step down to the
 * muted ink rather than fading*, which is 31c's point — "a 35% day is
 * unreadable, and both still have to be legible to be useful" — and they stay
 * pickable, because a cross-month range is the ordinary case.
 *
 * The month is rendered by whoever needs one: the range panel puts two side by
 * side, a form would use a single one. It holds no state of its own — the
 * caller owns the range and which month is on screen — so the same component
 * serves a filter, a form field and a sheet without a mode flag.
 */

/** 31a's 34px cells, and 31c's 30px inline cell. */
export type CellSize = "compact" | "comfortable"

const CELL: Record<CellSize, string> = {
  compact: "h-[30px] text-[11.5px]",
  comfortable: "h-[34px] text-[12.5px]",
}

function Step({
  onClick,
  direction,
  label,
  className,
}: {
  onClick: () => void
  direction: "prev" | "next"
  label: string
  className?: string
}) {
  const Icon = direction === "prev" ? ChevronLeft : ChevronRight
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        "flex size-[26px] flex-none items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
        className,
      )}
    >
      <Icon className="size-[15px]" />
    </button>
  )
}

export function CalendarMonth({
  month,
  from,
  to,
  today,
  onPick,
  onHoverDay,
  focusedDay,
  onPrev,
  onNext,
  prevClassName,
  nextClassName,
  size = "comfortable",
  className,
}: {
  month: YearMonth
  /** The range as it should read right now, hover preview included. */
  from?: string
  to?: string
  /** Marked with a hairline ring, never a fill — 31c. */
  today?: string
  onPick: (iso: string) => void
  onHoverDay?: (iso?: string) => void
  /**
   * The one day in the grid that is a tab stop. A month is a single stop, not
   * forty-two: you arrow around inside it, which is how a calendar is read, and
   * one Tab still leaves it.
   *
   * Only the month that *owns* the day answers to it. A borrowed day is drawn
   * in two grids when two months are on screen, and both claiming the tab stop
   * would put two of them in the page's tab order for one date.
   */
  focusedDay?: string
  /** 31a puts the two steppers at the panel's outer edges, so each is optional. */
  onPrev?: () => void
  onNext?: () => void
  /**
   * Per-stepper classes. `@3xl:invisible` on the next one is how the range
   * panel keeps a way forward while it is a single month wide and hands that
   * job back to the second month once there is one — hidden rather than
   * removed, so the header's slots and the centred label do not move.
   */
  prevClassName?: string
  nextClassName?: string
  size?: CellSize
  className?: string
}) {
  const days = monthDays(month)
  const band = bandShape(days, from, to)
  const title = monthLabel(month)

  return (
    <div className={cn("w-[266px] flex-none", className)}>
      {/*
        The label stays centred whether or not this month carries a stepper, so
        both months keep a 26px slot on each side.
      */}
      <div className="mb-1.5 flex h-[26px] items-center gap-1.5">
        {onPrev ? (
          <Step
            direction="prev"
            onClick={onPrev}
            label="Previous month"
            className={prevClassName}
          />
        ) : (
          <span aria-hidden className="w-[26px] flex-none" />
        )}
        <span className="flex-1 text-center text-[13px] font-medium leading-none">
          {title}
        </span>
        {onNext ? (
          <Step
            direction="next"
            onClick={onNext}
            label="Next month"
            className={nextClassName}
          />
        ) : (
          <span aria-hidden className="w-[26px] flex-none" />
        )}
      </div>

      {/*
        A plain grid, not an ARIA `grid`: that role wants rows between it and
        its cells, and the week rows here are a `grid-template`, not elements.
        Each day is a button reading its own full date instead.
      */}
      <div aria-label={title} className="grid grid-cols-7">
        {WEEKDAYS.map((weekday) => (
          <span
            key={weekday}
            aria-hidden
            className="flex h-[26px] items-center justify-center text-[11px] leading-none text-muted-foreground/70"
          >
            {weekday}
          </span>
        ))}

        {days.map((day, index) => {
          const shape = band[index]
          const isToday = day.iso === today
          return (
            <button
              key={day.iso}
              type="button"
              // In-month only, so one date is one keyboard target however many
              // grids happen to draw it.
              data-day={day.inMonth ? day.iso : undefined}
              tabIndex={
                focusedDay === undefined
                  ? 0
                  : day.inMonth && day.iso === focusedDay
                    ? 0
                    : -1
              }
              aria-label={dayLabel(day.iso)}
              aria-pressed={shape.state === "bound"}
              onClick={() => onPick(day.iso)}
              onMouseEnter={() => onHoverDay?.(day.iso)}
              onFocus={() => onHoverDay?.(day.iso)}
              className={cn(
                "flex items-center justify-center font-mono leading-none transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary",
                CELL[size],
                shape.roundLeft && "rounded-l-md",
                shape.roundRight && "rounded-r-md",
                shape.state === "bound" && "bg-primary font-medium text-primary-foreground",
                shape.state === "in" && "bg-primary/[0.08] text-foreground",
                shape.state === "plain" && [
                  "hover:bg-foreground/[0.06]",
                  // Out-of-month days step down one ink, and take the band's
                  // ink back the moment they are inside the range.
                  day.inMonth ? "text-foreground" : "text-muted-foreground",
                ],
                // A ring, so it never competes with the selection's fill.
                isToday &&
                  shape.state === "plain" &&
                  "text-brand-ink ring-1 ring-inset ring-primary/35 rounded-md",
              )}
            >
              {day.date}
            </button>
          )
        })}
      </div>
    </div>
  )
}
